import {from as observableFrom, Observable} from 'rxjs';

import {take, map, mergeMap} from 'rxjs/operators';
import {Injectable, Injector} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot} from '@angular/router';
import {getRouteParam} from '../utilities/Util';
import {UserService} from '../services/UserService';
import {User} from '../services/api.client';
import {UserType} from '../services/api.client';
import {SystemService} from '../services/SystemService';
import {SecurityService} from '../services/SecurityService';

@Injectable()
export class CanEditUserGuard {

    currentUser$: Observable<User>;

    constructor(private injector: Injector, private system: SystemService, private _securityService: SecurityService) {
        this.currentUser$ = this._securityService.currentUser$;
    }

    canActivate(route: ActivatedRouteSnapshot,
                state: RouterStateSnapshot): Observable<boolean> {
        // Redirect to the shared not-authorized page instead of silently cancelling the
        // navigation (which left a manual deep-link on a blank white screen).
        const deny = (): boolean => {
            this.injector.get(Router).navigate(['/not-authorized']).then();
            return false;
        };

        return this.currentUser$.pipe(
            take(1),
            mergeMap((currentUser) => {
                const id = getRouteParam(route, 'id');
                if (id === 'add') {
                    const canCreate = currentUser.userType === UserType.SystemAdmin ||
                        currentUser.userType === UserType.AgencyAdmin ||
                        currentUser.userType === UserType.RegionAdmin;
                    return observableFrom([canCreate ? true : deny()]);
                }
                // system admin or self edit
                if (currentUser.userType === UserType.SystemAdmin || currentUser.id === id) {
                    return observableFrom([true]);
                }
                // users, viewers and fundAdmins can't edit other users
                if (currentUser.userType === UserType.User ||
                    currentUser.userType === UserType.Viewer ||
                    currentUser.userType === UserType.FundAdmin) {
                    return observableFrom([deny()]);
                }
                // AgencyAdmin / RegionAdmin: fetch the target and defer to the shared
                // predicate (they may only edit Users/Viewers within their own agency).
                return this.injector.get(UserService).getUserById(id).pipe(
                    map((user: User) => this.system.canEditUser(user) ? true : deny()));
            }),);

    }
}
